import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { setShowSelected, updateSearchValue } from '@common-reducers/ContactTableReducer';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useWaContactsLazyLoad } from '@extension/context/content/hooks/useWaContactsLazyLoad';
import { loadAllContactsAndChatsInExtensionThunk, toggleContactSelectionThunk, selectContactsByLabelThunk } from '@extension/context/content/thunks/WhatsappThunks';
import { setImportedContactsArray, selectFilteredMergedContactList } from '@common-reducers/WhatsAppReducer';
import { selectDoesContactExistInBoard } from '@common-reducers/TaskSelectors';
import { getIsContactChecked, getWaContactDisplayLabel } from '@common-reducers/WhatsAppSelectors';
import { resolveContactLabel } from './channel-name-resolver';
import { nameOrFormattedPhone, resolveTypedRecipient, type TypedRecipient } from '@common/utils/phone-validation-utils';
import { selectChannelNameIndex } from './unified-contact-row';
import { Search, RefreshCw, Loader2, X, Plus, User, Users, Megaphone, LayoutGrid, CheckCheck, Tag, ChevronDown } from 'lucide-react';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Popper } from '@mui/material';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { isExtensionContext } from '@common-services/detect-context';
import { DBWaContactsCacheThunks, labelSelectByQuery } from '@common-reducers/DBServiceThunks';
import VirtuosoFooter from '@extension/context/content/components/add-scheduled-message-button/virtuoso-footer';
import UnifiedContactRow, { getContactSerializedId } from './unified-contact-row';
import { pickerBodyState, shouldClearSearchAfterSelect, type PickerBodyState } from './contact-selector-behavior';
import { resolveContactPickerEmptyState } from './contact-picker-empty-state';
import { selectConnectedEnginesState } from '@common-reducers/ConnectedEnginesSlice';
import { formatLastSyncedLabel } from './contact-sync-status';
import { resyncContacts, syncHintFor, ContactResyncDeps } from './contact-resync';
import ContactAvatar from '@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import { WaContact } from '@common/models/waContact';
import { MinimalContact } from '@common-reducers/WhatsAppSelectors';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { selectDefaultPhoneCountry } from '@common-reducers/UserSelectors';
import { createSelector } from '@reduxjs/toolkit';
import { excludeArchivedFromBulk } from './contact-bulk-select';

export interface SelectedContact {
    id: string;
    name?: string;
    displayName?: string;
    imgUrl?: string;
}

export type ContactTypeFilter = 'all' | 'contacts' | 'groups' | 'channels';

export interface UnifiedContactSelectorProps {
    multiSelect?: boolean;
    // When true, the indicator becomes a radio button and the caller is expected
    // to treat each click as a replacement (not a toggle). The selector itself
    // is dumb about that — it just renders the right shape and emits onContactClick.
    singleSelect?: boolean;
    showRefreshButton?: boolean;
    showSearch?: boolean;
    showTypeFilter?: boolean;
    displaySelectedAsChips?: boolean;
    collapsible?: boolean;
    allowFreeInput?: boolean;
    /** A typed recipient, already resolved to its international chat id. */
    onFreeInputAdd?: (recipient: TypedRecipient) => void;
    height?: number | string;
    className?: string;
    /** Extra classes for the search box (the chip field), e.g. a borderless filled look. */
    searchBoxClassName?: string;
    searchPlaceholder?: string;
    externalSearchValue?: string;
    onSearchChange?: (value: string) => void;
    selectedContactIds?: string[];
    selectedContacts?: SelectedContact[];
    onContactClick?: (contact: WaContact) => void;
    onContactRemove?: (contactId: string) => void;
    onClearAll?: () => void;
    useReduxSelection?: boolean;
    helperText?: string;
    /**
     * FE#305: the audience-import picker opts in to an actionable empty state.
     * When there are no contacts AND no live WhatsApp connection, show a
     * "connect WhatsApp" prompt (optionally with the CTA below) instead of the
     * generic "No contacts available", so the create no longer fails silently.
     */
    promptConnectWhenEmpty?: boolean;
    /** Invoked by the connect-WhatsApp CTA in the empty state (e.g. navigate to /connections). */
    onConnectWhatsApp?: () => void;
}

// Memoized selector: only recomputes when importedContactsArray actually changes
const selectReduxSelectedIds = createSelector(
    [(state: any) => state.WhatsAppReducer.importedContactsArray],
    (arr: any[]) => (arr || []).map((c: any) => c.whatsappId)
);

// Stable empty ref so the select-all selector never returns a fresh [] (avoids re-render churn).
const EMPTY_CONTACTS: any[] = [];

const VirtuosoItem = ({ children, ...props }: any) => <div {...props} style={{ height: '72px', width: '100%' }}>{children}</div>;
const VIRTUOSO_COMPONENTS = { Item: VirtuosoItem, Footer: VirtuosoFooter };
const VIRTUOSO_COMPONENTS_NO_FOOTER = { Item: VirtuosoItem };

// Resolves per-contact selection state into plain props for UnifiedContactRow.
const ResolvedContactRow: FC<{
    contact: WaContact | MinimalContact;
    index: number;
    showCheckbox: boolean;
    singleSelect?: boolean;
    useReduxSelection: boolean;
    onClick?: (contact: WaContact) => void;
    activeSelectedIds: string[];
}> = memo(({ contact, index, showCheckbox, singleSelect, useReduxSelection, onClick, activeSelectedIds }) => {
    const dispatch = useAppDispatch();
    const contactId = getContactSerializedId(contact) || '';
    const wid = (contact as any)?.whatsappId || contactId;

    const reduxIsChecked = useAppSelector(state =>
        useReduxSelection ? getIsContactChecked(state, wid) : false
    );
    const existsInBoard = useAppSelector(state =>
        useReduxSelection ? selectDoesContactExistInBoard(state, wid) : false
    );

    const isSelected = useReduxSelection ? (reduxIsChecked || existsInBoard) : activeSelectedIds.includes(contactId);
    const isDisabled = useReduxSelection && existsInBoard;

    const handleClick = useCallback((c: WaContact) => {
        if (useReduxSelection) {
            dispatch(toggleContactSelectionThunk({ contact: c }));
        } else {
            onClick?.(c);
        }
    }, [useReduxSelection, dispatch, onClick]);

    return (
        <UnifiedContactRow
            contact={contact}
            index={index}
            showCheckbox={showCheckbox}
            singleSelect={singleSelect}
            isSelected={isSelected}
            isDisabled={isDisabled}
            onClick={handleClick}
        />
    );
});
ResolvedContactRow.displayName = 'ResolvedContactRow';

export const UnifiedContactSelector: FC<UnifiedContactSelectorProps> = ({
    multiSelect = false,
    singleSelect = false,
    showRefreshButton = false,
    showSearch = true,
    showTypeFilter = false,
    displaySelectedAsChips = false,
    collapsible = false,
    allowFreeInput = false,
    onFreeInputAdd,
    height = 400,
    className,
    searchBoxClassName,
    searchPlaceholder,
    externalSearchValue,
    onSearchChange,
    selectedContactIds = [],
    selectedContacts = [],
    onContactClick,
    onContactRemove,
    onClearAll,
    useReduxSelection = false,
    helperText,
    promptConnectWhenEmpty = false,
    onConnectWhatsApp,
}) => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const isExtension = isExtensionContext();
    // #164: resolve channel names for the selected-chip labels the same way the
    // list rows do — so a picked channel never shows as a "+120363…" number.
    const channelNameIndex = useAppSelector(selectChannelNameIndex);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const popoverContentRef = useRef<HTMLDivElement>(null);

    // When the popover is too narrow to fit "Contacts / Groups / Channels"
    // plus the "Show Selected" toggle, collapse to icon-only buttons with
    // tooltips.
    const [isNarrowFilterRow, setIsNarrowFilterRow] = useState(false);
    // Width of the search-bar anchor — used to make the portaled popover the
    // same width as the input.
    const [anchorWidth, setAnchorWidth] = useState<number>(0);

    // Redux state
    const reduxShowSelected = useAppSelector(state => state.ContactTableReducer.showSelected);
    const reduxSearchValue = useAppSelector(state => state.ContactTableReducer.filterValue);
    const reduxSelectedIds = useAppSelector(selectReduxSelectedIds);

    // #140 — the user's own WhatsApp labels (custom, non-system), for one-click
    // "add everyone labelled X" bulk selection. Only meaningful in redux-selection mode.
    const userLabels = useAppSelector(state => labelSelectByQuery(
        state.DBLabelReducer,
        { type: 'custom', $or: [{ deleted: { $exists: false } }, { deleted: false }] },
        [],
        { order: 1 },
    ));

    const [localSearchValue, setLocalSearchValue] = useState('');
    const [localShowSelected, setLocalShowSelected] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    // FE#216: timestamp of the last successful "refresh contacts from WhatsApp"
    // (ms epoch), rendered as a relative "last synced" hint next to the search.
    const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
    // #297: the last refresh failed or came back partial -> show a retry hint instead of "Last synced".
    const [lastAttemptFailed, setLastAttemptFailed] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [typeFilter, setTypeFilter] = useState<ContactTypeFilter>('all');

    // #140 — "Add by label" inline dropdown open state + outside-click close.
    const [labelMenuOpen, setLabelMenuOpen] = useState(false);
    const labelMenuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!labelMenuOpen) return;
        // composedPath() pierces the extension shadow DOM so an outside click in
        // any shadow root still closes the menu.
        const onPointerDown = (e: Event) => {
            const path = (e as unknown as { composedPath?: () => EventTarget[] }).composedPath?.() ?? [];
            if (labelMenuRef.current && !path.includes(labelMenuRef.current)) {
                setLabelMenuOpen(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [labelMenuOpen]);

    // Resolve Redux vs local once
    const searchValue = externalSearchValue ?? (useReduxSelection ? reduxSearchValue : localSearchValue);
    const showSelectedActive = useReduxSelection ? reduxShowSelected : localShowSelected;
    const activeSelectedIds = useReduxSelection ? reduxSelectedIds : selectedContactIds;
    const selectedCount = activeSelectedIds.length;
    const isFocused = isExpanded || isSearchFocused;
    // A phone number in any common form, or a pasted chat id ("…@g.us").
    const allowToAdd = allowFreeInput && /^(\+?[\d\s().-]+|\S+@\S+)$/.test(searchValue.trim());

    // A typed number that matches no contact is offered as a contact row of
    // its own, showing the INTERNATIONAL number it will be saved as (a local
    // "054…" is read in the user's own country), so what is saved is what the
    // user saw. One it cannot read asks for the country code instead.
    const phoneCountry = useAppSelector(selectDefaultPhoneCountry);
    const typedRecipient = useMemo(
        () => (allowToAdd ? resolveTypedRecipient(searchValue, phoneCountry) : null),
        [allowToAdd, searchValue, phoneCountry],
    );
    const freeInputRow = useMemo(
        () => typedRecipient && {
            id: { _serialized: typedRecipient.chatId },
            name: typedRecipient.label,
            displayName: typedRecipient.label,
        },
        [typedRecipient],
    );

    const setSearchValue = useCallback((value: string) => {
        if (onSearchChange) onSearchChange(value);
        else if (useReduxSelection) dispatch(updateSearchValue(value));
        else setLocalSearchValue(value);
    }, [onSearchChange, useReduxSelection, dispatch]);

    const addFreeInput = useCallback(() => {
        if (!onFreeInputAdd || !typedRecipient) return;
        onFreeInputAdd(typedRecipient);
        setSearchValue('');
    }, [onFreeInputAdd, typedRecipient, setSearchValue]);

    const setShowSelectedActive = useCallback((checked: boolean) => {
        if (useReduxSelection) dispatch(setShowSelected(checked));
        else setLocalShowSelected(checked);
    }, [useReduxSelection, dispatch]);

    // --- Select all / Deselect all (filter-aware) ---
    // Only for the redux-backed multi-select modal (audience/contacts import).
    // Selects every contact matching the CURRENT search + type filter, so it
    // respects whatever the user is looking at rather than blindly grabbing
    // the whole address book.
    const selectAllEnabled = multiSelect && useReduxSelection;
    const filteredMergedForSelectAll = useAppSelector(state =>
        selectAllEnabled ? selectFilteredMergedContactList(state, searchValue) : EMPTY_CONTACTS
    );
    const reduxImportedContacts = useAppSelector(state => state.WhatsAppReducer.importedContactsArray);
    const selectableFilteredContacts = useMemo(() => {
        if (!selectAllEnabled) return EMPTY_CONTACTS;
        // #294: archived chats are searchable and pickable one by one, but a bulk add skips them.
        const bulkSelectable = excludeArchivedFromBulk(filteredMergedForSelectAll);
        if (!showTypeFilter || typeFilter === 'all') return bulkSelectable;
        return bulkSelectable.filter((c: any) => {
            const wid: string = c?.whatsappId || '';
            if (typeFilter === 'contacts') return wid.endsWith('@c.us');
            if (typeFilter === 'groups') return chatKindOf(wid) === 'group';
            if (typeFilter === 'channels') return chatKindOf(wid) === 'channel';
            return true;
        });
    }, [selectAllEnabled, showTypeFilter, typeFilter, filteredMergedForSelectAll]);
    const allFilteredSelected = selectableFilteredContacts.length > 0
        && selectableFilteredContacts.every((c: any) => activeSelectedIds.includes(c.whatsappId));
    const handleToggleSelectAllFiltered = useCallback(() => {
        if (!selectAllEnabled) return;
        const filteredIdSet = new Set(selectableFilteredContacts.map((c: any) => c.whatsappId));
        if (allFilteredSelected) {
            // Deselect only the currently-filtered contacts, keep the rest.
            dispatch(setImportedContactsArray((reduxImportedContacts || []).filter((c: any) => !filteredIdSet.has(c.whatsappId))));
        } else {
            // Add the filtered contacts on top of the existing selection.
            const merged = new Map((reduxImportedContacts || []).map((c: any) => [c.whatsappId, c]));
            for (const c of selectableFilteredContacts as any[]) {
                if (c.whatsappId && !merged.has(c.whatsappId)) merged.set(c.whatsappId, c);
            }
            dispatch(setImportedContactsArray(Array.from(merged.values())));
        }
    }, [selectAllEnabled, selectableFilteredContacts, allFilteredSelected, reduxImportedContacts, dispatch]);

    // Track the anchor (search bar) width. The popover is portaled to the
    // body — we sync its width to the anchor so it visually matches the
    // input, and we also use this width to flip the filter row into
    // icon-only mode before "Show Selected" can wrap onto two lines.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const apply = (width: number) => {
            setAnchorWidth(width);
            // Threshold sits above the trigger-dialog inner width (~540px) so
            // the icon-only mode actually triggers in that context. Wider
            // hosts (Scheduled Message Editor on a regular page) stay full.
            setIsNarrowFilterRow(width < 620);
        };

        apply(el.offsetWidth);

        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(([entry]) => apply(entry.contentRect.width));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Click outside (collapsible) — must check the portaled popover content
    // too, since it lives outside `containerRef` in the DOM tree.
    useEffect(() => {
        if (!collapsible || !isExpanded) return;
        const handleClickOutside: EventListener = (event) => {
            const path = event.composedPath?.() || [];
            const target = (path[0] || event.target) as Node;
            const inAnchor = !!containerRef.current?.contains(target)
                || (containerRef.current ? path.includes(containerRef.current as EventTarget) : false);
            const inPopover = !!popoverContentRef.current?.contains(target)
                || (popoverContentRef.current ? path.includes(popoverContentRef.current as EventTarget) : false);
            if (!inAnchor && !inPopover) {
                setIsExpanded(false);
            }
        };
        const root = containerRef.current?.getRootNode() as Document | ShadowRoot | undefined;
        const listenTarget = (root && (root instanceof Document || root instanceof ShadowRoot)) ? root : document;
        listenTarget.addEventListener('mousedown', handleClickOutside, true);
        listenTarget.addEventListener('touchstart', handleClickOutside);
        if (listenTarget !== document) {
            window.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('touchstart', handleClickOutside);
        }
        return () => {
            listenTarget.removeEventListener('mousedown', handleClickOutside, true);
            listenTarget.removeEventListener('touchstart', handleClickOutside);
            if (listenTarget !== document) {
                window.removeEventListener('mousedown', handleClickOutside);
                window.removeEventListener('touchstart', handleClickOutside);
            }
        };
    }, [collapsible, isExpanded]);

    // Lazy load
    const { filteredList, handleEndReached, handleRangeChanged, itemsLoading, isItemsLoaded, shouldGetMoreContacts, totalCount, hasMoreItems } =
        useWaContactsLazyLoad({ searchFilter: searchValue, typeFilter: showTypeFilter ? typeFilter : 'all' });

    // Prepend any pre-selected contacts that aren't in the paginated cache.
    // Without this, the website (which paginates contacts) shows "No contacts
    // available" under Show Selected when the user's currently-stored contact
    // simply hasn't been loaded yet. The extension already loads everything
    // upfront so this is a no-op there.
    // Not while searching (outside Show Selected): an unrelated selected chip
    // would otherwise fill the list and hide the "not in your contacts" row.
    const augmentedList = useMemo(() => {
        if (!selectedContacts?.length) return filteredList;
        if (searchValue && !showSelectedActive) return filteredList;
        const filteredIds = new Set(
            filteredList
                .map(c => getContactSerializedId(c))
                .filter(Boolean) as string[]
        );
        const orphans = selectedContacts
            .filter(sc => sc?.id && !filteredIds.has(sc.id))
            .map(sc => ({
                id: { _serialized: sc.id },
                name: sc.name || sc.displayName,
                displayName: sc.displayName || sc.name,
                imgUrl: sc.imgUrl,
            })) as any[];
        return orphans.length ? [...orphans, ...filteredList] : filteredList;
    }, [filteredList, selectedContacts, searchValue, showSelectedActive]);

    // Show selected filter
    const displayList = useMemo(() => {
        if (!showSelectedActive) return augmentedList;
        return augmentedList.filter(c => {
            const id = getContactSerializedId(c);
            return id && activeSelectedIds.includes(id);
        });
    }, [augmentedList, showSelectedActive, activeSelectedIds]);

    const isLoading = displayList.length === 0 && (itemsLoading || (!isItemsLoaded && shouldGetMoreContacts));
    const isEmpty = (isItemsLoaded || isExtension) && displayList.length === 0 && !isLoading;

    // FE#305: pick the empty-state variant. Only the audience-import picker
    // (promptConnectWhenEmpty) surfaces the actionable "connect WhatsApp" state;
    // every other picker keeps the generic copy via the helper's fallthrough.
    const hasWhatsAppConnection = useAppSelector(selectConnectedEnginesState).hasWhatsAppConnection;
    const emptyStateVariant = resolveContactPickerEmptyState({
        hasSearch: !!searchValue,
        hasWhatsAppConnection,
        promptConnectWhenEmpty,
    });

    // One decision for the list area, so the free-input row is not knocked back to
    // the spinner by the refetch every keystroke starts (see pickerBodyState).
    const freeRowAvailable = emptyStateVariant === 'search-miss' && !!freeInputRow && !!onFreeInputAdd;
    const lastBodyStateRef = useRef<PickerBodyState | null>(null);
    const bodyState = pickerBodyState({ isLoading, isEmpty, freeRowAvailable, lastShown: lastBodyStateRef.current });
    const showFreeRow = bodyState === 'free-row';
    useEffect(() => { lastBodyStateRef.current = bodyState; }, [bodyState]);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        setLastAttemptFailed(false);
        try {
            // FE#216: AWAIT the real re-sync (extension → direct WA pull; web →
            // the `$forceContactsUpdate: true` cache find that makes the backend
            // do a live WhatsApp pull + repopulate the doc, re-reading page 1 for
            // the current query). Awaiting means the spinner reflects the real
            // round-trip (the previous web path was fire-and-forget). `resyncContacts`
            // stamps only on success — a failed/aborted sync returns null and leaves
            // the previous last-synced timestamp intact. See contact-resync.test.ts.
            const stampedAt = await resyncContacts(
                {
                    dispatch: dispatch as unknown as ContactResyncDeps['dispatch'],
                    webFind: DBWaContactsCacheThunks.find,
                    extensionResync: loadAllContactsAndChatsInExtensionThunk,
                },
                { searchValue, typeFilter, isExtension },
                Date.now(),
            );
            if (stampedAt != null) setLastSyncedAt(stampedAt);
            else setLastAttemptFailed(true);
        } finally {
            setIsRefreshing(false);
        }
    }, [dispatch, searchValue, isExtension, typeFilter]);

    // FE#216: relative "last synced" label. Recomputed each render off the
    // stamped timestamp (good enough — it refreshes on the next re-render, e.g.
    // the next keystroke or a subsequent sync; no live ticking interval needed).
    const lastSyncedLabel = useMemo(
        () => formatLastSyncedLabel(lastSyncedAt, Date.now()),
        [lastSyncedAt],
    );
    const syncHint = syncHintFor({ isRefreshing, lastAttemptFailed, lastSyncedLabel });

    // In chip mode, clear the search text once a TERMINAL selection is made so the
    // freshly-added chip isn't left sitting next to a stale query (matches the
    // free-input add behavior above). Never in multi-select, where the query is the
    // user's narrowing and has to survive each pick — see shouldClearSearchAfterSelect.
    const handleContactClick = useCallback((contact: WaContact) => {
        onContactClick?.(contact);
        if (shouldClearSearchAfterSelect({ displaySelectedAsChips, multiSelect })) setSearchValue('');
    }, [onContactClick, displaySelectedAsChips, multiSelect, setSearchValue]);

    const renderContact = useCallback((index: number, contact: MinimalContact) => (
        <ResolvedContactRow
            contact={contact}
            index={index}
            showCheckbox={multiSelect || singleSelect}
            singleSelect={singleSelect}
            useReduxSelection={useReduxSelection}
            onClick={handleContactClick}
            activeSelectedIds={activeSelectedIds}
        />
    ), [multiSelect, singleSelect, useReduxSelection, handleContactClick, activeSelectedIds]);

    const virtuosoStyle = useMemo(() => ({
        height: typeof height === 'number' ? `${height}px` : height,
        minWidth: '100%',
        overflowY: 'auto' as const,
    }), [height]);

    const filterRowEl = (showTypeFilter || multiSelect) ? (
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-2">
                {showTypeFilter && ([
                    { key: 'all', label: t('contacts.filterAll', 'All'), icon: LayoutGrid },
                    { key: 'contacts', label: t('contacts.filterContacts', 'Contacts'), icon: User },
                    { key: 'groups', label: t('contacts.filterGroups', 'Groups'), icon: Users },
                    { key: 'channels', label: t('contacts.filterChannels', 'Channels'), icon: Megaphone },
                ] as const).map(({ key, label, icon: Icon }) => (
                    <button
                        key={key}
                        onClick={() => setTypeFilter(key)}
                        title={isNarrowFilterRow ? label : undefined}
                        aria-label={label}
                        className={cn(
                            "flex items-center gap-1.5 rounded-full text-xs font-medium transition-all duration-200 border",
                            isNarrowFilterRow ? "p-1.5" : "px-3 py-1.5",
                            typeFilter === key
                                ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                                : "bg-[var(--color-surface)] text-[var(--color-text-muted)] border-[var(--color-border)] hover:border-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                        )}
                    >
                        {Icon && <Icon className="h-3.5 w-3.5" />}
                        {!isNarrowFilterRow && label}
                    </button>
                ))}
                {/* #140 — one-click "add everyone labelled X". Redux-selection mode only,
                    and only when the user actually has custom WhatsApp labels.
                    NOTE: intentionally a plain inline (state-toggled, non-portaled) menu
                    rather than the Radix DropdownMenu — this selector renders inside a
                    modal dialog + the extension shadow DOM, where a portaled Radix menu's
                    DismissableLayer conflicts with the dialog's modal layer and the menu
                    won't open. The sibling filter buttons are plain buttons for the same
                    reason. */}
                {useReduxSelection && userLabels.length > 0 && (
                    <div ref={labelMenuRef} className="relative">
                        <button
                            type="button"
                            title={t('contacts.byLabel', 'Add by label')}
                            aria-label={t('contacts.byLabel', 'Add by label')}
                            aria-expanded={labelMenuOpen}
                            onClick={() => setLabelMenuOpen(o => !o)}
                            className={cn(
                                "flex items-center gap-1.5 rounded-full text-xs font-medium transition-all duration-200 border",
                                isNarrowFilterRow ? "p-1.5" : "px-3 py-1.5",
                                "bg-[var(--color-surface)] text-[var(--color-text-muted)] border-[var(--color-border)] hover:border-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                            )}
                        >
                            <Tag className="h-3.5 w-3.5" />
                            {!isNarrowFilterRow && t('contacts.byLabel', 'Add by label')}
                            {!isNarrowFilterRow && <ChevronDown className="h-3 w-3" />}
                        </button>
                        {labelMenuOpen && (
                            <div
                                role="menu"
                                className="absolute left-0 top-[calc(100%+4px)] z-[1000] max-h-64 min-w-[10rem] overflow-y-auto rounded-md border p-1 shadow-md"
                                style={{
                                    backgroundColor: 'var(--color-surface, #ffffff)',
                                    color: 'var(--color-text, #1a202c)',
                                    borderColor: 'var(--color-border, #e5e7eb)',
                                }}
                            >
                                {userLabels.map((label: any) => (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        key={label._id}
                                        onClick={() => {
                                            dispatch(selectContactsByLabelThunk({ labelId: label._id }));
                                            setLabelMenuOpen(false);
                                        }}
                                        className="flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-[var(--color-surface-muted)]"
                                    >
                                        <Tag className="h-3.5 w-3.5 shrink-0" />
                                        {label.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {selectAllEnabled && (
                    <button
                        onClick={handleToggleSelectAllFiltered}
                        disabled={selectableFilteredContacts.length === 0}
                        title={allFilteredSelected ? t('importContacts.deselectAll', 'Deselect all') : t('importContacts.selectAll', 'Select all')}
                        aria-label={allFilteredSelected ? t('importContacts.deselectAll', 'Deselect all') : t('importContacts.selectAll', 'Select all')}
                        className={cn(
                            "flex items-center gap-1.5 rounded-full text-xs font-medium transition-all duration-200 border disabled:opacity-40 disabled:cursor-not-allowed",
                            isNarrowFilterRow ? "p-1.5" : "px-3 py-1.5",
                            allFilteredSelected
                                ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                                : "bg-[var(--color-surface)] text-[var(--color-text-muted)] border-[var(--color-border)] hover:border-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                        )}
                    >
                        <CheckCheck className="h-3.5 w-3.5" />
                        {!isNarrowFilterRow && (allFilteredSelected ? t('importContacts.deselectAll', 'Deselect all') : t('importContacts.selectAll', 'Select all'))}
                    </button>
                )}
            </div>
            {multiSelect && (
                <div
                    onClick={() => setShowSelectedActive(!showSelectedActive)}
                    title={isNarrowFilterRow ? t('importContacts.showSelected', 'Show Selected') : undefined}
                    className={cn(
                        "relative flex items-center gap-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 cursor-pointer select-none",
                        isNarrowFilterRow ? "px-2 py-1.5" : "px-3 py-1.5",
                    )}
                >
                    <Switch checked={showSelectedActive} onCheckedChange={setShowSelectedActive} className="data-[state=checked]:bg-blue-500 pointer-events-none" />
                    {!isNarrowFilterRow && (
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            {t('importContacts.showSelected', 'Show Selected')}
                        </span>
                    )}
                    {selectedCount > 0 && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (useReduxSelection) {
                                    dispatch(setImportedContactsArray([]));
                                }
                                onClearAll?.();
                                setShowSelectedActive(false);
                            }}
                            title={t('importContacts.clearAll', 'Clear all')}
                            className="relative -mr-1 flex items-center justify-center h-5 w-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-semibold hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-all duration-200 group"
                        >
                            <span className="group-hover:hidden">{selectedCount}</span>
                            <X className="h-3 w-3 hidden group-hover:block" />
                        </button>
                    )}
                </div>
            )}
        </div>
    ) : null;

    return (
        <div
            ref={containerRef}
            className={cn("flex flex-col", collapsible ? "relative w-full" : "flex-1 min-h-0", className)}
        >
            {showSearch && (
                <>
                    <div
                        onClick={() => inputRef.current?.focus()}
                        className={cn(
                            "flex items-center gap-2 px-3 min-h-[48px] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg transition-all duration-200 cursor-text",
                            isFocused && "ring-1 ring-[var(--color-border)] border-[var(--color-text-muted)]",
                            searchBoxClassName
                        )}
                    >
                        <Search className={cn("h-5 w-5 flex-shrink-0 transition-colors duration-200", isFocused ? "text-[var(--color-text)]" : "text-[var(--color-text-muted)]")} />

                        <div className="flex-1 flex flex-wrap items-center gap-1.5 py-2 min-w-0">
                            {displaySelectedAsChips && selectedContacts.map(contact => (
                                <div key={contact.id} className="flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full bg-[var(--color-primary-light)] border border-[var(--color-primary)] text-[var(--color-primary)] text-sm font-medium">
                                    <ContactAvatar size={22} textSize={8} contactId={contact.id} profilePicURL={contact.imgUrl} />
                                    {/* A typed number not in contacts is stored as raw digits: show it formatted, like the add row. */}
                                    <span className="max-w-[160px] truncate">{nameOrFormattedPhone(resolveContactLabel(contact, channelNameIndex, getWaContactDisplayLabel(contact), t('contacts.channelFallback', 'Channel')), contact.id)}</span>
                                    {onContactRemove && (
                                        <button onClick={e => { e.stopPropagation(); onContactRemove(contact.id); }} className="ml-0.5 rounded-full p-0.5 hover:text-red-500 hover:bg-red-50 transition-all duration-200">
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            ))}

                            <div className={cn("flex-1", (!displaySelectedAsChips || selectedContacts.length === 0) ? "min-w-[200px]" : "min-w-[120px]")}>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={searchValue}
                                    onChange={e => {
                                        setSearchValue(e.target.value);
                                    }}
                                    onKeyDown={e => {
                                        // Enter picks the "not in your contacts" row, when it is the one on offer.
                                        if (e.key === 'Enter' && showFreeRow) { e.preventDefault(); addFreeInput(); }
                                    }}
                                    onFocus={() => { if (collapsible) setIsExpanded(true); setIsSearchFocused(true); }}
                                    onBlur={() => setIsSearchFocused(false)}
                                    placeholder={searchPlaceholder ?? t('importContacts.searchPlaceholder', 'Search contacts...')}
                                    autoComplete="off"
                                    className="w-full border-none outline-none bg-transparent text-sm font-medium text-[var(--color-text)]"
                                    style={{ textAlign: 'start', direction: 'inherit' }}
                                />
                            </div>
                        </div>

                        {showRefreshButton && (
                            <button
                                onClick={e => { e.stopPropagation(); handleRefresh(); }}
                                disabled={isRefreshing}
                                className={cn("p-1.5 rounded-md flex-shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:bg-[var(--color-surface-muted)] transition-all duration-200", isRefreshing && "animate-spin")}
                            >
                                <RefreshCw className="h-4 w-4" />
                            </button>
                        )}

                    </div>

                    {/* FE#216: loading / last-synced hint for the "refresh contacts
                        from WhatsApp" button. Web-only (the button is web-only). */}
                    {showRefreshButton && syncHint && (
                        <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] mt-1 px-1 text-start">
                            {syncHint === 'syncing' ? (
                                <>
                                    <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                                    <span>{t('contacts.syncing', 'Syncing contacts from WhatsApp…')}</span>
                                </>
                            ) : syncHint === 'failed' ? (
                                <span role="status" className="text-[var(--color-danger,#dc2626)]">{t('contacts.refreshFailed', "Couldn't refresh contacts from WhatsApp. Try again.")}</span>
                            ) : (
                                <span>{t('contacts.lastSynced', 'Last synced')} {lastSyncedLabel}</span>
                            )}
                        </div>
                    )}

                    {helperText && (
                        <div className="text-xs text-[var(--color-text-muted)] mt-1 px-1 text-start">* {helperText}</div>
                    )}

                </>
            )}

            {/* The refresh button normally lives in the search box; with an external search
                input (showSearch=false, e.g. the chatbot send node) it gets its own slim row. */}
            {!showSearch && showRefreshButton && (
                <div className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] px-1 py-1 text-start">
                    <button
                        type="button"
                        onClick={e => { e.stopPropagation(); handleRefresh(); }}
                        disabled={isRefreshing}
                        aria-label={t('contacts.syncing', 'Syncing contacts from WhatsApp…')}
                        className={cn("p-1 rounded-md flex-shrink-0 hover:text-[var(--color-primary)] hover:bg-[var(--color-surface-muted)] transition-all duration-200", isRefreshing && "animate-spin")}
                    >
                        <RefreshCw className="h-4 w-4" />
                    </button>
                    {isRefreshing ? (
                        <span>{t('contacts.syncing', 'Syncing contacts from WhatsApp…')}</span>
                    ) : syncHint === 'failed' ? (
                        <span role="status" className="text-[var(--color-danger,#dc2626)]">{t('contacts.refreshFailed', "Couldn't refresh contacts from WhatsApp. Try again.")}</span>
                    ) : lastSyncedLabel ? (
                        <span>{t('contacts.lastSynced', 'Last synced')} {lastSyncedLabel}</span>
                    ) : null}
                </div>
            )}

            {(!collapsible || isExpanded) && (() => {
                const inner = (
                    <>
                        {filterRowEl}
                        {bodyState === 'loading' && (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                            </div>
                        )}
                        {bodyState === 'empty' && emptyStateVariant === 'connect-whatsapp' && (
                            <div className="flex flex-col items-center justify-center gap-3 py-10 px-6 text-center" role="status">
                                <div className="rounded-full bg-[var(--color-surface-muted)] p-3">
                                    <Users className="h-7 w-7 text-[var(--color-text-muted)]" />
                                </div>
                                <p className="text-sm font-semibold text-[var(--color-text)]">
                                    {t('importContacts.emptyConnectTitle', 'Connect WhatsApp to build an audience from your contacts')}
                                </p>
                                <p className="text-xs text-[var(--color-text-muted)] max-w-sm">
                                    {t('importContacts.emptyConnectBody', 'Your WhatsApp contacts show up here once a connection is active. Connect WhatsApp to import them into an audience.')}
                                </p>
                                {onConnectWhatsApp && (
                                    <button
                                        type="button"
                                        onClick={onConnectWhatsApp}
                                        className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                                    >
                                        <Plus className="h-4 w-4" />
                                        {t('importContacts.emptyConnectCta', 'Connect WhatsApp')}
                                    </button>
                                )}
                            </div>
                        )}
                        {showFreeRow && freeInputRow && (
                            <UnifiedContactRow
                                contact={freeInputRow}
                                showCheckbox={multiSelect || singleSelect}
                                singleSelect={singleSelect}
                                onClick={addFreeInput}
                                subtitle={t('contacts.notInContacts', 'Not in your contacts — you can still send to it')}
                            />
                        )}
                        {bodyState === 'empty' && emptyStateVariant !== 'connect-whatsapp' && (
                            <div className="flex flex-col items-center justify-center py-8 px-4">
                                <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
                                    {emptyStateVariant === 'search-miss'
                                        ? allowToAdd && onFreeInputAdd
                                            ? t('contacts.needCountryCode', 'Enter the full number with its country code, e.g. +972 54 123 4567')
                                            : t('importContacts.emptyNoMatch', 'No contacts found')
                                        : t('importContacts.emptyNoContacts', 'No contacts available')}
                                </p>
                            </div>
                        )}
                        {bodyState === 'list' && (
                            <div className="flex-1 min-h-0 overflow-hidden">
                                <Virtuoso
                                    components={showSelectedActive ? VIRTUOSO_COMPONENTS_NO_FOOTER : VIRTUOSO_COMPONENTS}
                                    increaseViewportBy={250}
                                    data={displayList}
                                    endReached={handleEndReached}
                                    rangeChanged={handleRangeChanged}
                                    style={virtuosoStyle}
                                    totalCount={hasMoreItems ? Math.max(totalCount, displayList.length + 1) : displayList.length}
                                    itemContent={renderContact}
                                />
                            </div>
                        )}
                    </>
                );

                if (collapsible) {
                    return (
                        <Popper
                            open={isExpanded}
                            anchorEl={containerRef.current}
                            container={containerRef.current?.closest('[role="dialog"]') as HTMLElement | undefined}
                            placement="bottom-start"
                            sx={{ zIndex: 1500 }}
                            modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                        >
                            <div
                                ref={popoverContentRef}
                                style={{ width: anchorWidth || undefined }}
                                className="flex flex-col rounded-xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15),0_8px_32px_rgba(0,0,0,0.1)] border border-[var(--color-border)] bg-[var(--color-surface)] max-h-[420px]"
                                onClick={e => e.stopPropagation()}
                            >
                                {inner}
                            </div>
                        </Popper>
                    );
                }
                return (
                    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                        {inner}
                    </div>
                );
            })()}
        </div>
    );
};

export default UnifiedContactSelector;
